import { api } from './index';
import { PaginatedResponse, PaginationParams } from '../types';
import { resolveBackendAssetUrl } from '../utils/apiBaseUrl';

export interface BuildingBanner {
  id: string;
  buildingId: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  linkUrl?: string | null;
  displayOrder: number;
  isActive: boolean;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: string; name: string };
}

export interface BuildingNewsArticle {
  id: string;
  buildingId: string;
  title: string;
  summary?: string | null;
  content?: string | null;
  coverImage?: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isPinned: boolean;
  publishedAt?: string | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: string; name: string };
}

export const buildingHomeContentApi = {
  uploadImage: async (file: File, buildingId: string, folder: 'banners' | 'news' = 'banners') => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ url: string }>(
      `/building-home-content/upload?buildingId=${buildingId}&folder=${folder}`,
      formData,
    );
    return response.data;
  },

  getBanners: async (buildingId: string, params?: PaginationParams) => {
    const response = await api.get<PaginatedResponse<BuildingBanner>>('/building-home-content/banners', {
      params: { buildingId, ...params },
    });
    return response.data;
  },

  getBanner: async (id: string) => {
    const response = await api.get<BuildingBanner>(`/building-home-content/banners/${id}`);
    return response.data;
  },

  createBanner: async (data: Partial<BuildingBanner>) => {
    const response = await api.post<BuildingBanner>('/building-home-content/banners', data);
    return response.data;
  },

  updateBanner: async (id: string, data: Partial<BuildingBanner>) => {
    const response = await api.patch<BuildingBanner>(`/building-home-content/banners/${id}`, data);
    return response.data;
  },

  toggleBannerStatus: async (id: string) => {
    const response = await api.patch<BuildingBanner>(`/building-home-content/banners/${id}/toggle-status`);
    return response.data;
  },

  deleteBanner: async (id: string) => {
    const response = await api.delete(`/building-home-content/banners/${id}`);
    return response.data;
  },

  getNewsArticles: async (
    buildingId: string,
    params?: PaginationParams & { status?: string },
  ) => {
    const response = await api.get<PaginatedResponse<BuildingNewsArticle>>('/building-home-content/news', {
      params: { buildingId, ...params },
    });
    return response.data;
  },

  getNewsArticle: async (id: string) => {
    const response = await api.get<BuildingNewsArticle>(`/building-home-content/news/${id}`);
    return response.data;
  },

  createNewsArticle: async (data: Partial<BuildingNewsArticle>) => {
    const response = await api.post<BuildingNewsArticle>('/building-home-content/news', data);
    return response.data;
  },

  updateNewsArticle: async (id: string, data: Partial<BuildingNewsArticle>) => {
    const response = await api.patch<BuildingNewsArticle>(`/building-home-content/news/${id}`, data);
    return response.data;
  },

  deleteNewsArticle: async (id: string) => {
    const response = await api.delete(`/building-home-content/news/${id}`);
    return response.data;
  },
};

export const resolveMediaUrl = (url?: string | null) => {
  if (!url) return '';
  if (url.startsWith('s3://')) return url;
  return resolveBackendAssetUrl(url);
};
